import React from 'react'

import { bemJoin } from 'bem-join'
import { Justification, Space, SpeechBubbleWithAvatar, Stack } from 'component-library-next'
import Lottie from 'lottie-react'

import robotNeutral from '@app/assets/robot-neutral.svg'
import robotSleep from '@app/assets/robot-sleep.json'
import { useIntl } from '@app/intl/Intl'
import { AdvancePaymentsCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/AdvancePaymentsCheckResults'
import { BenchmarkDeviationsCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/BenchmarkDeviationsCheckResults'
import { DeadlinesCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/DeadlinesCheckResults'
import { DistributionFormulaCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/DistributionFormulaCheckResults'
import { InvoiceItemsCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/InvoiceItemsCheckResults'
import { ServicePeriodCheckResults } from '@app/modules/Messages/Components/UtilityBillAICheckResults/CheckResults/ServicePeriodCheckResults'
import { useUtilityBillResultsDataQuery } from '@app/queries/UtilityBillResultsData/UtilityBillResultsData.queries'

import './UtilityBillAICheckResults.scss'

type Props = {
  documentId: number
  documentName: string
}

export const UtilityBillAICheckResults = ({ documentId, documentName }: Props) => {
  const bem = bemJoin('utility-bill-ai-check-results')
  const { formatMessage } = useIntl()
  const { data, loading } = useUtilityBillResultsDataQuery({
    variables: { documentId },
    fetchPolicy: 'network-only'
  })

  if (loading || !data?.customerDocument) {
    return null
  }

  const utilityBillAnalysis = data.customerDocument.utilityBillAnalysis

  const resultContent = utilityBillAnalysis ? (
    <Stack gap={Stack.gap.large}>
      <InvoiceItemsCheckResults check={utilityBillAnalysis.checks.invoiceItems} />
      <BenchmarkDeviationsCheckResults check={utilityBillAnalysis.checks.benchmarkDeviations} />
      <DistributionFormulaCheckResults check={utilityBillAnalysis.checks.distributionFormula} />
      <DeadlinesCheckResults check={utilityBillAnalysis.checks.deadlines} />
      <AdvancePaymentsCheckResults check={utilityBillAnalysis.checks.advancePayments} />
      <ServicePeriodCheckResults check={utilityBillAnalysis.checks.servicePeriod} />
    </Stack>
  ) : (
    <Stack justification={Justification.center} gap={Space.small}>
      <Lottie
        className={bem('no-result-animation')}
        animationData={robotSleep}
        loop={true}
        autoplay={true}
      />
      <div className={bem('no-result-text')}>
        <div className={bem('no-result-title')}>
          {formatMessage({ id: 'UtilityBillAICheckResults.NoResult.Title' })}
        </div>
        <div className={bem('no-result-description')}>
          {formatMessage({ id: 'UtilityBillAICheckResults.NoResult.Description' })}
        </div>
      </div>
    </Stack>
  )

  return (
    <div className={bem()}>
      <SpeechBubbleWithAvatar
        title={
          <span>
            {formatMessage(
              { id: 'UtilityBillAICheckResults.Title' },
              { strong: (value) => <strong>{value}</strong>, documentName }
            )}
          </span>
        }
        avatarImage={robotNeutral}
      >
        {resultContent}
      </SpeechBubbleWithAvatar>
    </div>
  )
}
